import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addCart, removeOrder } from '../../redux/menu/cartSlice';
import { FaInfoCircle } from 'react-icons/fa';
import VegNonVegIcon from '../Common/VegNonVegIcons';

const FoodItem = ({ image, name, description, price, id, currency, options, is_veg, variants, productOptions, sizes, ProductVariants, categoryName, categoryDescription, showAddToCart = true, highlightInfoIcon = false, highlight = false, ...rest }) => {
    const dispatch = useDispatch();
    const cartItems = useSelector((state) => state.cart.cart);
    const [variantQuantities, setVariantQuantities] = useState({});

    const [showTooltip, setShowTooltip] = useState(false);
    
    // Memoize variant options to prevent infinite re-renders
    const finalVariantOptions = useMemo(() => {
        // Check for ProductVariants field first (your API field)
        let variantOptions = ProductVariants || [];
        
        // If no ProductVariants, check other possible field names
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = options || variants || productOptions || sizes || 
                            (rest?.variants) || (rest?.options) || (rest?.productOptions) || (rest?.sizes) || [];
        }
        
        // If variants is a string, try to parse it as JSON
        if (typeof variantOptions === 'string') {
            try {
                variantOptions = JSON.parse(variantOptions);
            } catch (e) {
                variantOptions = [];
            }
        }
        
        // Check for other common field names as fallback
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = rest?.product_variants || rest?.productVariants || rest?.item_variants || rest?.itemVariants || 
                            rest?.productOptions || rest?.product_options || rest?.variants || rest?.options || [];
        }
        
        return variantOptions || [];
    }, [ProductVariants, options, variants, productOptions, sizes, rest]);

    const finalHasVariants = useMemo(() => {
        return finalVariantOptions && finalVariantOptions.length > 0;
    }, [finalVariantOptions]);
    
    // Sync local state with cart state
    useEffect(() => {
        const syncQuantities = () => {
            const newQuantities = {};
            
            // For products with variants
            if (finalHasVariants) {
                finalVariantOptions.forEach(variant => {
                    const cartItem = cartItems.find(item => item.id === `${id}-${variant.id}`);
                    if (cartItem) {
                        newQuantities[variant.id] = cartItem.quantity;
                    }
                });
            } else {
                // For regular products without variants
                const cartItem = cartItems.find(item => item.id === id);
                if (cartItem) {
                    newQuantities['base'] = cartItem.quantity;
                }
            }
            
            // Only update state if quantities have actually changed
            setVariantQuantities(prevQuantities => {
                const hasChanged = JSON.stringify(prevQuantities) !== JSON.stringify(newQuantities);
                return hasChanged ? newQuantities : prevQuantities;
            });
        };
        
        syncQuantities();
    }, [cartItems, id, finalHasVariants, finalVariantOptions]);
    
    // Get total quantity across all variants
    const getTotalQuantity = () => {
        return Object.values(variantQuantities).reduce((total, qty) => total + qty, 0);
    };
    
    // Get total price across all variants
    const getTotalPrice = () => {
        let total = 0;
        finalVariantOptions.forEach(variant => {
            const qty = variantQuantities[variant.id] || 0;
            total += parseFloat(variant.price) * qty;
        });
        return total;
    };

    const handleVariantQuantityChange = (variantId, newQuantity) => {
        if (newQuantity >= 0) {
            const updatedQuantities = { ...variantQuantities };
            
            if (newQuantity > 0) {
                updatedQuantities[variantId] = newQuantity;
                setVariantQuantities(updatedQuantities);
                
                // Add to cart
                if (finalHasVariants) {
                    const variant = finalVariantOptions.find(v => v.id === variantId);
                    const cartItem = {
                        id: `${id}-${variantId}`,
                        name: `${name} (${variant.name})`,
                        price: parseFloat(variant.price),
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg,
                        variant: variant
                    };
                    dispatch(addCart(cartItem));
                } else {
                    // Regular product without variants
                    const cartItem = {
                        id: id,
                        name: name,
                        price: parseFloat(price),
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg
                    };
                    dispatch(addCart(cartItem));
                }
            } else if (newQuantity === 0) {
                delete updatedQuantities[variantId];
                setVariantQuantities(updatedQuantities);
                
                // Remove from cart
                const cartId = finalHasVariants ? `${id}-${variantId}` : id;
                dispatch(removeOrder(cartId));
            }
        }
    };
    // Regular card layout
    if (!highlight) {
        return (
            <div className="bg-white border border-gray-100 transition transform duration-700 hover:shadow-lg md:hover:shadow-xl hover:scale-[1.02] md:hover:scale-105 p-2.5 md:p-3 lg:p-4 rounded-lg relative min-h-[140px] sm:min-h-[160px] md:min-h-[180px] lg:min-h-[200px] flex flex-col justify-between items-center w-full">
                {/* Info Tooltip Icon */}
                <div className="absolute top-1.5 md:top-2 left-1.5 md:left-2 z-20">
                    <div className="relative">
                        <button
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                            onTouchStart={() => setShowTooltip(!showTooltip)}
                            className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center border transition-colors ${
                                highlightInfoIcon 
                                    ? 'bg-white border-black hover:bg-gray-100 shadow-lg' 
                                    : 'bg-gray-600/70 border-gray-500/50 hover:bg-gray-600'
                            }`}
                        >
                            <FaInfoCircle className={`w-2.5 h-2.5 md:w-3 md:h-3 ${
                                highlightInfoIcon 
                                    ? 'text-black' 
                                    : 'text-gray-200'
                            }`} />
                        </button>
                        
                        {/* Tooltip Content */}
                        {showTooltip && (
                            <div className="absolute top-6 md:top-8 left-0 z-30 bg-black text-white rounded-lg p-1.5 md:p-2 shadow-lg min-w-[120px] md:min-w-[150px] max-w-[150px] md:max-w-[200px] border border-gray-600">
                                <div className="text-xs font-semibold mb-1 text-blue-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {categoryName || 'Category'}
                                </div>
                                {categoryDescription && (
                                    <div className="text-xs text-gray-200 leading-relaxed">
                                        {categoryDescription}
                                    </div>
                                )}
                                {/* Tooltip Arrow */}
                                <div className="absolute -top-2 left-2 md:left-3 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-black"></div>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Veg/Non-Veg Symbol */}
                <div className="absolute top-1.5 md:top-2 right-1.5 md:right-2 z-10">
                    <VegNonVegIcon is_veg={is_veg} className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <img className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-32 xl:h-32 mx-auto transform transition duration-300 hover:scale-105 object-cover" src={`${image}`} alt="" />
                <div className="flex flex-col items-center mt-1.5 md:mt-2 mb-1.5 md:mb-2 lg:my-3 space-y-1 md:space-y-2 w-full">
                    <h1 className="text-gray-900 poppins text-xs sm:text-sm md:text-base lg:text-lg text-center font-medium mb-0.5 md:mb-1 px-1 md:px-2 leading-tight">{name}</h1>
                    
                    {/* Variant Selection with Individual Quantity Controls */}
                    {finalHasVariants && (
                        <div className="flex flex-col gap-2 md:gap-3 justify-center mb-1 md:mb-2 w-full">
                            {finalVariantOptions.map((variant) => {
                                const variantQty = variantQuantities[variant.id] || 0;
                                return (
                                    <div key={variant.id} className="flex items-center justify-between bg-gray-50 rounded-md md:rounded-lg p-1.5 md:p-2">
                                        <div className="flex-1">
                                            <div className="text-xs font-medium text-gray-700 truncate">{variant.name}</div>
                                            <div className="text-xs text-primary font-bold">{currency} {parseFloat(variant.price).toFixed(2)}</div>
                                        </div>
                                        
                                        {showAddToCart && (
                                            variantQty === 0 ? (
                                                <button 
                                                    onClick={() => handleVariantQuantityChange(variant.id, 1)}
                                                    className="bg-primary text-white px-2 py-1 md:px-4 md:py-2 text-xs md:text-sm font-semibold rounded-full hover:bg-primary-dark transition-colors shadow-sm md:shadow-md"
                                                >
                                                    ADD
                                                </button>
                                            ) : (
                                                <div className="flex items-center space-x-1.5 md:space-x-2">
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                        className="w-5 h-5 md:w-6 md:h-6 bg-white text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-100 transition-all duration-200 text-xs md:text-sm font-bold shadow-sm border border-gray-200"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-xs md:text-sm font-bold text-gray-800 min-w-[15px] md:min-w-[20px] text-center">{variantQty}</span>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                        className="w-5 h-5 md:w-6 md:h-6 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-dark transition-all duration-200 text-xs md:text-sm font-bold shadow-sm"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Total Price Display - Only for products with variants */}
                    {finalHasVariants && getTotalQuantity() > 0 && (
                        <div className="text-center mt-1 md:mt-2">
                            <div className="text-xs text-gray-600">Total: {getTotalQuantity()} items</div>
                            <div className={`text-gray-900 poppins text-sm md:text-base font-bold ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {currency} {getTotalPrice().toFixed(2)}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Product (No Variants) */}
                    {!finalHasVariants && (
                        <div className="flex flex-col items-center mt-1 md:mt-2">
                            <div className={`text-gray-900 poppins text-xs sm:text-sm md:text-base font-bold ${currency === 'AED' ? 'currency-aed' : ''} text-center mb-1 md:mb-2`}>
                                {currency} {(parseFloat(price) * (variantQuantities['base'] || 1)).toFixed(2)}
                            </div>
                            
                            {showAddToCart && (
                                variantQuantities['base'] === 0 || !variantQuantities['base'] ? (
                                    <button 
                                        onClick={() => handleVariantQuantityChange('base', 1)}
                                        className="bg-primary text-white px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 focus:outline-none poppins rounded-full transform transition duration-300 hover:scale-105 hover:bg-primary-dark text-xs sm:text-sm md:text-base font-medium shadow-sm md:shadow-md"
                                    >
                                        Add to Cart
                                    </button>
                                ) : (
                                    <div className="flex items-center space-x-2 md:space-x-3 bg-gray-50 rounded-full px-2 py-1.5 md:px-3 md:py-2 shadow-sm">
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] - 1)}
                                            className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-white text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-100 transition-all duration-200 text-sm md:text-lg font-bold shadow-sm border border-gray-200"
                                        >
                                            -
                                        </button>
                                        <span className="text-sm sm:text-base md:text-lg font-bold text-gray-800 min-w-[20px] sm:min-w-[25px] md:min-w-[30px] text-center">{variantQuantities['base']}</span>
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] + 1)}
                                            className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-dark transition-all duration-200 text-sm md:text-lg font-bold shadow-sm"
                                        >
                                            +
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    // Highlighted card layout (spans 2 columns)
    return (
        <div className="col-span-2 bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 border-2 border-primary/30 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative overflow-hidden shadow-xl md:shadow-2xl hover:shadow-2xl md:hover:shadow-3xl transition-all duration-500 hover:scale-[1.01] md:hover:scale-[1.02]">
            {/* Highlight Badge */}
            <div className="absolute top-2 md:top-3 left-2 md:left-3 z-20">
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 md:px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                    ⭐ FEATURED
                </div>
            </div>

            {/* Info Tooltip Icon */}
            <div className="absolute bottom-2 md:bottom-3 right-2 md:right-3 z-20">
                <div className="relative">
                    <button
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onTouchStart={() => setShowTooltip(!showTooltip)}
                        className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center border transition-colors ${
                            highlightInfoIcon 
                                ? 'bg-white border-black hover:bg-gray-100 shadow-lg' 
                                : 'bg-gray-600/70 border-gray-500/50 hover:bg-gray-600'
                        }`}
                    >
                        <FaInfoCircle className={`w-3 h-3 md:w-4 md:h-4 ${
                            highlightInfoIcon 
                                ? 'text-black' 
                                : 'text-gray-200'
                        }`} />
                    </button>
                    
                    {/* Tooltip Content */}
                    {showTooltip && (
                        <div className="absolute bottom-8 md:bottom-10 right-0 z-30 bg-black text-white rounded-lg p-2 md:p-3 shadow-lg min-w-[150px] md:min-w-[200px] max-w-[200px] md:max-w-[250px] border border-gray-600">
                            <div className="text-xs md:text-sm font-semibold mb-1 md:mb-2 text-blue-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                {categoryName || 'Category'}
                            </div>
                            {categoryDescription && (
                                <div className="text-xs md:text-sm text-gray-200 leading-relaxed">
                                    {categoryDescription}
                                </div>
                            )}
                            {/* Tooltip Arrow */}
                            <div className="absolute -bottom-2 right-3 md:right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Veg/Non-Veg Symbol */}
            <div className="absolute top-2 md:top-3 right-2 md:right-3 z-10">
                <VegNonVegIcon is_veg={is_veg} className="w-5 h-5 md:w-6 md:h-6" />
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-4 lg:gap-6">
                {/* Product Image */}
                <div className="flex-shrink-0">
                    <img 
                        className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 xl:w-48 xl:h-48 object-cover rounded-xl md:rounded-2xl shadow-lg md:shadow-xl transform transition duration-300 hover:scale-105" 
                        src={`${image}`} 
                        alt={name}
                    />
                </div>

                {/* Product Details */}
                <div className="flex-1 text-center md:text-left">
                    <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 poppins mb-1 md:mb-2 lg:mb-3">
                        {name}
                    </h1>
                    
                    {description && (
                        <p className="text-gray-600 text-xs sm:text-sm md:text-base mb-2 md:mb-3 lg:mb-4 leading-relaxed">
                            {description}
                        </p>
                    )}

                    {/* Variant Selection with Individual Quantity Controls */}
                    {finalHasVariants && (
                        <div className="space-y-2 md:space-y-3 mb-2 md:mb-3 lg:mb-4">
                            {finalVariantOptions.map((variant) => {
                                const variantQty = variantQuantities[variant.id] || 0;
                                return (
                                    <div key={variant.id} className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm md:shadow-md">
                                        <div className="flex-1">
                                            <div className="text-xs sm:text-sm md:text-base font-semibold text-gray-800">{variant.name}</div>
                                            <div className="text-xs sm:text-sm md:text-base text-primary font-bold">{currency} {parseFloat(variant.price).toFixed(2)}</div>
                                        </div>
                                        
                                        {showAddToCart && (
                                            variantQty === 0 ? (
                                                <button 
                                                    onClick={() => handleVariantQuantityChange(variant.id, 1)}
                                                    className="bg-primary text-white px-3 py-1.5 md:px-6 md:py-2 lg:px-8 lg:py-3 text-xs sm:text-sm md:text-base font-semibold rounded-full hover:bg-primary-dark transition-colors shadow-sm md:shadow-md"
                                                >
                                                    ADD
                                                </button>
                                            ) : (
                                                <div className="flex items-center space-x-2 md:space-x-3">
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                        className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-white text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-100 transition-all duration-200 text-sm md:text-lg font-bold shadow-sm border border-gray-200"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 min-w-[20px] sm:min-w-[25px] md:min-w-[30px] text-center">{variantQty}</span>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                        className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-dark transition-all duration-200 text-sm md:text-lg font-bold shadow-sm"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Total Price Display - Only for products with variants */}
                    {finalHasVariants && getTotalQuantity() > 0 && (
                        <div className="text-center md:text-left mb-2 md:mb-3 lg:mb-4">
                            <div className="text-xs sm:text-sm md:text-base text-gray-600 mb-1">Total: {getTotalQuantity()} items</div>
                            <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary poppins ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {currency} {getTotalPrice().toFixed(2)}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Product (No Variants) */}
                    {!finalHasVariants && (
                        <div className="text-center md:text-left">
                            <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary poppins mb-2 md:mb-3 lg:mb-4 ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {currency} {(parseFloat(price) * (variantQuantities['base'] || 1)).toFixed(2)}
                            </div>
                            
                            {showAddToCart && (
                                variantQuantities['base'] === 0 || !variantQuantities['base'] ? (
                                    <button 
                                        onClick={() => handleVariantQuantityChange('base', 1)}
                                        className="bg-primary text-white px-4 py-2 sm:px-6 sm:py-2.5 md:px-8 md:py-3 lg:px-12 lg:py-4 text-sm sm:text-base md:text-lg lg:text-xl font-semibold rounded-full hover:bg-primary-dark transition-colors shadow-sm md:shadow-md"
                                    >
                                        Add to Cart
                                    </button>
                                ) : (
                                    <div className="flex items-center justify-center md:justify-start space-x-3 md:space-x-4 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl px-4 py-3 md:px-6 md:py-4 shadow-md md:shadow-lg">
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] - 1)}
                                            className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white text-gray-700 rounded-full flex items-center justify-center hover:bg-gray-100 transition-all duration-200 text-lg md:text-xl font-bold shadow-sm border border-gray-200"
                                        >
                                            -
                                        </button>
                                        <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 min-w-[30px] sm:min-w-[35px] md:min-w-[40px] text-center">{variantQuantities['base']}</span>
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] + 1)}
                                            className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-dark transition-all duration-200 text-lg md:text-xl font-bold shadow-sm"
                                        >
                                            +
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default FoodItem
